import { AlertCircle, ArrowRight, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { AssessmentCriteria } from "@/components/guides/AssessmentCriteria";
import { DashboardLayout } from "@/layouts/DashboardLayout";

const SUBMIT_STEPS = [
  "Open Work Packages and choose the one marked OPEN FOR SUBMISSION.",
  "Read your brief carefully: each one is unique, drawn from a bank of 288M+ AI-generated research briefs.",
  "Write your report and export it as a PDF, maximum 1,200 words including references. Most strong submissions land around 800–900 words.",
  "Cite your sources: distinguish primary from secondary evidence and assess credibility and recency. If you used AI to research, draft or refine, disclose it alongside your references.",
  "Submit with a day to spare: each submission returns AI feedback in about 30 seconds, and your final submission (up to three in total) is the one that goes for sign-off.",
];

function SectionCard({
  label,
  title,
  accent = false,
  children,
}: {
  label: string;
  title: string;
  accent?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-[14px] border border-content-border bg-card px-7 py-[26px] ${accent ? "border-t-2 border-t-brand" : ""}`}
    >
      <div className="text-[11px] font-bold tracking-[.16em] text-content-muted">{label}</div>
      <h2 className="mt-1 text-[22px] font-bold tracking-[-.01em] text-card-foreground">{title}</h2>
      {children}
    </div>
  );
}

export default function HowToGetStartedPage() {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto flex flex-col gap-[26px]">
        {/* Header */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[.16em] text-content-muted">
            Guides
          </p>
          <h1 className="mt-1.5 text-3xl sm:text-4xl font-extrabold tracking-[-.015em] text-content-foreground">
            How to get started
          </h1>
          <p className="mt-1.5 max-w-2xl text-[15px] text-[#5C6E63]">
            Everything you need to submit your first work package with confidence: how to submit,
            and how your work is assessed.
          </p>
        </div>

        {/* Best feedback guide link */}
        <Link
          to="/marking-guide"
          className="flex w-full items-center gap-[18px] rounded-[14px] border border-content-border border-t-2 border-t-brand bg-card px-7 py-[22px] transition-colors hover:border-brand"
        >
          <div className="flex h-11 w-11 flex-none items-center justify-center rounded-[11px] bg-[#E9F1E6]">
            <BookOpen className="h-5 w-5 text-[#1E6F4F]" strokeWidth={1.7} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold tracking-[.16em] text-content-muted">
              FEEDBACK GUIDE
            </div>
            <div className="mt-0.5 text-[17px] font-bold text-card-foreground">
              How to get the best feedback
            </div>
            <div className="mt-0.5 text-[13.5px] text-[#5C6E63]">
              What low, medium and high scoring work looks like across the five criteria.
            </div>
          </div>
          <span className="hidden sm:flex flex-none items-center gap-2 text-xs font-bold tracking-[.1em] text-[#1E6F4F]">
            READ THE GUIDE
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
          </span>
        </Link>

        {/* Submitting */}
        <SectionCard label="SUBMITTING" title="How to submit a work package">
          <div className="mt-[18px] flex flex-col">
            {SUBMIT_STEPS.map((text, i) => (
              <div
                key={text}
                className="flex gap-4 border-b border-content-border/60 px-1 py-[13px]"
              >
                <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full border-[1.5px] border-brand text-[11.5px] font-bold text-[#1E6F4F]">
                  {i + 1}
                </span>
                <div className="pt-px text-sm leading-[1.6] text-[#28402F]">{text}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-start gap-3 rounded-[10px] border border-[#E4D3A1] bg-[#FBF6E7] px-[18px] py-3.5">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-[#8A6D1F]" strokeWidth={1.8} />
            <div className="text-[13px] leading-[1.6] text-[#6B5417]">
              <strong>One open window. Deadlines are 13:00 UK time.</strong> There's no separate
              draft deadline. You can submit up to three times: for AI feedback on your first two
              submissions, and a final version for sign-off. Submit with a day to spare so you have
              time to act on the feedback.
            </div>
          </div>
        </SectionCard>

        {/* Assessment */}
        <SectionCard label="FEEDBACK" title="How your work is assessed">
          <p className="mt-1.5 max-w-[680px] text-sm text-[#5C6E63]">
            Your work is assessed against five criteria, benchmarked against professional investment
            analysis standards. Every work package receives a final Executive Review by an
            experienced human before results are released. The pass threshold for this cohort is
            65%, and Work Package 1 is unweighted, so it never counts against you.
          </p>
          <AssessmentCriteria />
        </SectionCard>
      </div>
    </DashboardLayout>
  );
}
